import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  AllocationResult,
  TraceEvent,
  AgentId,
  ApprovalStatus,
  CheckpointData
} from '../../types';
import { apiService } from '../../services/api';

interface AnalysisState {

  result: AllocationResult | null;
  baseResult: AllocationResult | null;

  currentNode: AgentId | null;
  traces: TraceEvent[];
  agentStatuses: Record<AgentId, 'idle' | 'active' | 'completed'>;


  isRunning: boolean;
  runId: string | null;
  pendingScenarioId: string | null;
  whatifWeights: Record<string, number> | null;
  globalSupplyOverride: number | null;
  globalDemandOverride: number | null;
  parameterOverrides: Record<string, number> | null;
  batchOverrides: Record<string, Record<string, number>> | null;
  lastRunWasWhatif: boolean;

  // Checkpoint state
  pendingCheckpoint: CheckpointData | null;
  isTransitioning: boolean; // True when resuming from a gate but next node traces haven't arrived
  isResumeFlow: boolean;    // True during the entire duration of a resume run

  error: string | null;
}

// Canonical agent IDs for display
const initialAgentStatuses: Record<AgentId, 'idle' | 'active' | 'completed'> = {
  orchestrator: 'idle',
  strategist: 'idle',
  constraint: 'idle',
  optimizer: 'idle',
  compiler: 'idle',
};

// Map backend node names to frontend display agent IDs
const normalizeNodeName = (node: string): AgentId | null => {
  const n = node.toLowerCase();
  if (n === 'orchestrator') return 'orchestrator';
  if (n === 'strategist') return 'strategist';
  if (n === 'constraint_advisor') return 'constraint';
  if (n === 'distribution_optimizer') return 'optimizer';
  if (n === 'insight_compiler') return 'compiler';
  return null;
};

const initialState: AnalysisState = {
  result: null,
  baseResult: null,
  currentNode: null,
  traces: [],
  agentStatuses: { ...initialAgentStatuses },
  isRunning: false,
  runId: null,
  pendingScenarioId: null,
  whatifWeights: null,
  globalSupplyOverride: null,
  globalDemandOverride: null,
  parameterOverrides: null,
  batchOverrides: null,
  lastRunWasWhatif: false,
  pendingCheckpoint: null,
  isTransitioning: false,
  isResumeFlow: false,
  error: null,
};

export const approveAllocation = createAsyncThunk(
  'analysis/approveAllocation',
  async (runId: string, { rejectWithValue }) => {
    try {
      await apiService.approveAllocation(runId);
      return 'approved' as ApprovalStatus;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to approve allocation');
    }
  }
);

export const rejectAllocation = createAsyncThunk(
  'analysis/rejectAllocation',
  async (runId: string, { rejectWithValue }) => {
    try {
      await apiService.rejectAllocation(runId);
      return 'rejected' as ApprovalStatus;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to reject allocation');
    }
  }
);

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {

    startAnalysis(state, action: PayloadAction<{
      scenarioId: string;
      weights?: Record<string, number>;
      globalSupplyOverride?: number;
      globalDemandOverride?: number;
      parameterOverrides?: Record<string, number>;
      batchOverrides?: Record<string, Record<string, number>>;
    }>) {
      const { scenarioId, weights, globalSupplyOverride, globalDemandOverride, parameterOverrides, batchOverrides } = action.payload;

      const isWhatIf = weights || globalSupplyOverride || globalDemandOverride || parameterOverrides || batchOverrides;

      if (isWhatIf && state.result) {
        // Capture current result as baseline for comparison
        state.baseResult = JSON.parse(JSON.stringify(state.result));
      } else if (!isWhatIf) {
        // Fresh run, no comparison needed
        state.baseResult = null;
      }

      state.isRunning = true;
      state.runId = scenarioId;
      state.whatifWeights = weights || null;
      state.globalSupplyOverride = globalSupplyOverride || null;
      state.globalDemandOverride = globalDemandOverride || null;
      state.parameterOverrides = parameterOverrides || null;
      state.batchOverrides = batchOverrides || null;
      state.lastRunWasWhatif = !!weights || !!globalSupplyOverride || !!globalDemandOverride || !!parameterOverrides || !!batchOverrides;
      state.traces = [];
      state.result = null;
      state.error = null;
      state.currentNode = 'orchestrator';
      state.agentStatuses = { ...initialAgentStatuses, orchestrator: 'active' };
      state.isResumeFlow = false; // Fresh start
    },

    addTraceEvent(state, action: PayloadAction<TraceEvent>) {
      const event = action.payload;
      state.traces.push(event);
      state.isTransitioning = false; // Traces arrived, transition done

      if (!state.isRunning) return;

      const rawNode = event.node?.toLowerCase() || '';
      const isStrategistResumeSkip =
        state.isResumeFlow &&
        rawNode === 'strategist' &&
        event.event_type === 'skip';
      if (isStrategistResumeSkip) {
        // During resume, strategist is a bypassed checkpoint.
        // Keep focus on downstream execution nodes.
        state.agentStatuses.strategist = 'completed';
        return;
      }

      const node = normalizeNodeName(rawNode);

      if (node && node !== state.currentNode) {
        if (state.currentNode && state.agentStatuses[state.currentNode] !== undefined) {
          state.agentStatuses[state.currentNode] = 'completed';
        }
        state.currentNode = node;
        if (state.agentStatuses[node] !== undefined) {
          state.agentStatuses[node] = 'active';
        }
      }
    },

    completeAnalysis(state, action: PayloadAction<AllocationResult>) {
      state.isRunning = false;
      state.isTransitioning = false;  // Reset transitioning state
      state.result = action.payload;
      state.runId = null;

      // Mark all agents as completed
      Object.keys(state.agentStatuses).forEach((key) => {
        const agentKey = key as AgentId;
        state.agentStatuses[agentKey] = 'completed';
      });
      state.currentNode = null;  // No active node
      state.isResumeFlow = false;
    },

    setAnalysisError(state, action: PayloadAction<string>) {
      state.isRunning = false;
      state.error = action.payload;
      state.runId = null;
      state.isResumeFlow = false;
    },

    resetAnalysis(state) {
      state.traces = [];
      state.result = null;
      state.baseResult = null;
      state.error = null;
      state.currentNode = null;
      state.agentStatuses = { ...initialAgentStatuses };
      state.isResumeFlow = false;
    },

    setWhatifWeights(state, action: PayloadAction<Record<string, number> | null>) {
      state.whatifWeights = action.payload;
    },


    setPendingScenarioId(state, action: PayloadAction<string | null>) {
      state.pendingScenarioId = action.payload;
    },

    setAgentStatus(state, action: PayloadAction<{ agentId: AgentId; status: 'idle' | 'active' | 'completed' }>) {
      const { agentId, status } = action.payload;
      state.agentStatuses[agentId] = status;
    },

    // Checkpoint actions
    setCheckpoint(state, action: PayloadAction<CheckpointData>) {
      state.pendingCheckpoint = action.payload;
      state.isRunning = false;

      const rawNode = action.payload.current_node?.toLowerCase() || '';
      const node = normalizeNodeName(rawNode);
      if (node) {
        state.currentNode = node;
        state.agentStatuses[node] = 'active'; // Mark as active while waiting for review
      }
    },

    clearCheckpoint(state) {
      state.pendingCheckpoint = null;
      state.isTransitioning = true;
      state.isResumeFlow = true; // Resume start

      // Reset statuses for downstream agents to ensure they don't look "completed" from a previous run
      if (state.currentNode === 'strategist') {
        state.agentStatuses.strategist = 'completed';
        state.currentNode = null;
        state.agentStatuses.constraint = 'idle';
        state.agentStatuses.optimizer = 'idle';
        state.agentStatuses.compiler = 'idle';
      } else if (state.currentNode === 'constraint') {
        state.currentNode = null;
        state.agentStatuses.optimizer = 'idle';
        state.agentStatuses.compiler = 'idle';
      }
    },

    setIsRunning(state, action: PayloadAction<boolean>) {
      state.isRunning = action.payload;
    },

    loadHistoricalResult(state, action: PayloadAction<AllocationResult>) {
      state.isRunning = false;
      state.isTransitioning = false;
      state.result = action.payload;
      state.runId = null;
      state.error = null;
      state.pendingCheckpoint = null;
      state.currentNode = null;
      state.traces = [];
      state.lastRunWasWhatif = false;
      state.isResumeFlow = false;
      state.agentStatuses = { ...initialAgentStatuses };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(approveAllocation.fulfilled, (state, action) => {
        if (state.result) {
          state.result.approval_status = action.payload;
        }
      })
      .addCase(approveAllocation.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(rejectAllocation.fulfilled, (state, action) => {
        if (state.result) {
          state.result.approval_status = action.payload;
        }
      })
      .addCase(rejectAllocation.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  startAnalysis,
  addTraceEvent,
  completeAnalysis,
  setAnalysisError,
  resetAnalysis,
  setWhatifWeights,
  setPendingScenarioId,
  setAgentStatus,
  setCheckpoint,
  clearCheckpoint,
  setIsRunning,
  loadHistoricalResult,
} = analysisSlice.actions;

export default analysisSlice.reducer;
